import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWhoami } from '../../../stores/org';
import { isPlainLeftClick } from './plain-left-click';
import { resourceItemPath, type ResourceTarget } from './resource-navigator';
import { useResourceNavigator } from './use-resource-navigator';

export type ArtifactResourceLinkProps = Omit<
  React.ComponentPropsWithoutRef<'a'>,
  'href'
> & {
  /** The workspace resource this link points at. */
  to: ResourceTarget;
};

/**
 * A link to a workspace resource that stays inside the artifact panel when there
 * is one.
 *
 * Sibling to {@link ArtifactLink}, but for whole resources rather than the
 * in-stack drill-in views the panel pushes onto an artifact. A plain left-click
 * is routed through {@link useResourceNavigator}, so from inside a chat (or an
 * editor's side panel) the resource opens as a *sibling artifact* beside what the
 * user is already looking at instead of navigating away; ⌘/ctrl-click,
 * middle-click and "open in new tab" fall through to the resource's full page via
 * the anchor's `href`. With no panel around, it behaves like an ordinary `<Link>`.
 */
export const ArtifactResourceLink: React.FC<ArtifactResourceLinkProps> = ({
  to,
  onClick,
  children,
  ...anchorProps
}) => {
  const navigator = useResourceNavigator();
  const orgSlug = useWhoami((s) => s.org.slug);
  const href = resourceItemPath(orgSlug, to.resourceType, to.resourceId);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (
        event.defaultPrevented ||
        !isPlainLeftClick(event, anchorProps.target)
      ) {
        return;
      }
      event.preventDefault();
      navigator.open(to);
    },
    [onClick, anchorProps.target, navigator, to]
  );

  // Types with no full-page route can only open in a panel, so there is no `href`
  // to fall through to — render a bare anchor for those.
  if (href === null) {
    return (
      <a role="link" onClick={handleClick} {...anchorProps}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} onClick={handleClick} {...anchorProps}>
      {children}
    </Link>
  );
};
